/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {PeriodDetailModel} from '../../models/period/period-detail/period-detail.model';
import {BundleDetailModel} from '../../models/bundle/bundle-detail/bundle-detail.model';
import {SettlementDetailModel} from '../../models/settlement/settlement-detail/settlement-detail.model';
import {PeriodViewTabsEnum} from '../../bundles-main-page/data/period-view-tabs.enum';
import {PeriodTypeInfoChartModel} from '../../models/period-type-info-chart/period-type-info-chart.model';
import {PeriodTypeInfoChartBundleModel} from '../../models/period-type-info-chart/period-type-info-chart-bundle.model';
import {PeriodResultModel} from '../../models/period/period-result/period-result.model';

@Injectable({
    providedIn: 'root'
})

export class PeriodsViewsService {
    private periodsList: PeriodDetailModel[];
    private bundlesList: BundleDetailModel[];
    private settlementsList: SettlementDetailModel[];
    private currentView: PeriodViewTabsEnum;
    private periodTypeInfoChartData = new Map<string, PeriodTypeInfoChartModel>();
    private bundleToPeriodDetailHashMap = new Map<string, PeriodDetailModel>();
    private globalSearchCriteriaStatus: boolean;
    updateTableLinesByGlobalSearchButton = false; // Flag to refresh the table lines when a global search criteria button is clicked.

    public updatePeriodListWithPeriodDetail(periodDetail: PeriodDetailModel): void {
        const periodsListCopy: PeriodDetailModel[] = [...this.periodsList];

        const index = periodsListCopy.findIndex((element) => element.code === periodDetail.code);

        // findIndex returns -1 if no element was found
        if (index !== -1) {
            periodsListCopy[index] = periodDetail;

            this.updateViewsMappers(periodsListCopy);
        }
    }

    public updateViewsMappers(periods: PeriodDetailModel[]): void {
        const bundlesList: BundleDetailModel[] = [];
        const settlementsList: SettlementDetailModel[] = [];
        this.periodTypeInfoChartData = new Map<string, PeriodTypeInfoChartModel>();

        periods?.forEach((periodDetail) => {

            const bundleToSettlementsList = new Map<PeriodTypeInfoChartBundleModel, string[]>();
            const bundlesInsidePeriod: string[] = [];
            periodDetail.bundlesList?.forEach((bundleDetail) => {

                const settlementsIdentification: string[] = [];
                bundleDetail.bundlePositions.forEach((bundlePositionsDetail) => {

                    bundlePositionsDetail.settlementsList.forEach((settlementDetail) => {

                        settlementsList.push(settlementDetail);
                        settlementsIdentification.push(`${settlementDetail.settlementCode}-${settlementDetail.complaintCode}`);
                    });
                });
                bundlesInsidePeriod.push(bundleDetail.number);
                bundlesList.push(bundleDetail);
                this.bundleToPeriodDetailHashMap.set(bundleDetail.number, periodDetail);

                if (settlementsIdentification.length > 0) {
                    bundleToSettlementsList.set({
                        bundleNumber: bundleDetail?.number,
                        status: bundleDetail?.status?.code
                    }, settlementsIdentification);
                }
            });

            if (bundlesInsidePeriod.length > 0) {
                this.periodTypeInfoChartData.set(periodDetail.code, {
                    timePeriod: periodDetail.timePeriod,
                    periodType: periodDetail.periodType,
                    bundlesList: bundlesInsidePeriod,
                    settlementsList: bundleToSettlementsList
                });
            }
        });

        this.updatePeriodsList(periods ?? []);
        this.updateBundlesList(bundlesList);
        this.updateSettlementsList(settlementsList);
    }

    public getPeriodsList(): PeriodDetailModel[] {
        return this.periodsList;
    }

    public getBundlesList(): BundleDetailModel[] {
        return this.bundlesList;
    }

    public getSettlementsList(): SettlementDetailModel[] {
        return this.settlementsList;
    }

    public setCurrentView(view: PeriodViewTabsEnum): void {
        this.currentView = view;
    }

    public getCurrentView(): PeriodViewTabsEnum {
        return this.currentView;
    }

    public getPeriodDetailByBundleNumber(bundleNumber: string): PeriodDetailModel {
        return this.bundleToPeriodDetailHashMap.get(bundleNumber);
    }

    public getPeriodTypeInfoChartData(): Map<string, PeriodTypeInfoChartModel> {
        return this.periodTypeInfoChartData;
    }

    public isGlobalSearchCriteriaApplied(): boolean {
        return this.globalSearchCriteriaStatus;
    }

    public setGlobalSearchCriteriaStatus(hasSearchCriteriaApplied: boolean): void {
        this.globalSearchCriteriaStatus = hasSearchCriteriaApplied;
    }

    private updatePeriodsList(updatedPeriodsList: PeriodDetailModel[]): void {
        this.periodsList = [...updatedPeriodsList];
    }

    private updateBundlesList(updatedBundlesList: BundleDetailModel[]): void {
        this.bundlesList = [...updatedBundlesList];
    }

    private updateSettlementsList(updatedSettlementsList: SettlementDetailModel[]): void {
        this.settlementsList = [...updatedSettlementsList];
    }
}
